import React, { useState, useMemo, useEffect } from 'react';
import CartSection from '../components/cart/CartSection';
import RecommendedLessons from '../components/recommendations/RecommendedLessons';
import OrderSummary from '../components/checkout/OrderSummary';
import PaymentMethods from '../components/checkout/PaymentMethods';
import { useCart } from '../contexts/CartContext';
import { useLessons } from '../contexts/LessonsContext';
import { useUser } from '../contexts/UserContext';
import CartHeader from '../components/ui/CartHeader';

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const CheckoutPageContent = () => {
  const {
    lessons,
    recommendedLessons,
    expanded,
    activePayment,
    orderSummary,
    toggleLessonSelection,
    removeLesson,
    addLessonFromOnline,
    toggleSection,
    setPaymentMethod,
    handleCheckout,
    validateCheckoutSelection,
    refreshCart,
  } = useCart();

  const { markLessonsPurchased, canAddToCart } = useLessons();
  const { user } = useUser();
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const displaySummary = useMemo(() => {
    const selected = lessons.filter((l) => l.selected);
    const gefenSelected = selected.filter((l) => l.isGefen);

    // Wallet-first allocation (client-side mirror of the server, for
    // display only): each GEFEN lesson's price is covered from its own
    // program's wallet balance first; whatever is left — including public
    // lessons' full price — becomes the subtotal charged through the
    // chosen normal payment method (card / PayPal / general credit).
    const remainingWalletByProgram = {};
    let gefenWalletCovered = 0;

    const subtotal = selected.reduce((sum, l) => {
      const price = safeNumber(l.price);
      let walletPortion = 0;

      if (l.isGefen && l.programId != null) {
        if (!(l.programId in remainingWalletByProgram)) {
          remainingWalletByProgram[l.programId] = safeNumber(l.gefenWalletBalance);
        }
        walletPortion = Math.min(remainingWalletByProgram[l.programId], price);
        remainingWalletByProgram[l.programId] -= walletPortion;
      }

      gefenWalletCovered += walletPortion;
      return sum + Math.max(0, price - walletPortion);
    }, 0);

    const TAX_RATE = safeNumber(orderSummary?.TAX_RATE, 0.17);

    const creditBalance = Math.max(
      0,
      Number(user?.credit_balance ?? user?.creditBalance ?? 0)
    );
    const creditUsed = Math.min(creditBalance, subtotal);
    const remainingBeforeVat = Math.max(subtotal - creditUsed, 0);
    const vat = remainingBeforeVat > 0 ? remainingBeforeVat * TAX_RATE : 0;

    const gefenTotal = gefenSelected.reduce((sum, l) => sum + safeNumber(l.price), 0);
    const gefenProgramNames = Array.from(
      new Set(gefenSelected.map((l) => l.programName).filter(Boolean))
    );

    return {
      subtotal,
      TAX_RATE,
      creditBalance,
      creditUsed,
      remainingBeforeVat,
      vat,
      total: remainingBeforeVat + vat,
      selectedCount: selected.length,

      gefenSelectedCount: gefenSelected.length,
      gefenTotal,
      gefenWalletCovered,
      gefenShortfall: Math.max(0, gefenTotal - gefenWalletCovered),
      gefenProgramNames,
    };
  }, [lessons, user?.credit_balance, user?.creditBalance, orderSummary?.TAX_RATE]);

  useEffect(() => {
    refreshCart({ silent: false }).catch((err) => {
      console.error('[CheckoutPage] refreshCart failed on mount:', err);
    });
  }, [refreshCart]);

  const onCheckout = async (paymentMethodOrEvent) => {
    if (isCheckingOut) return;
    setIsCheckingOut(true);

    try {
      const refreshed = await refreshCart({ silent: true });

      const currentLessons = Array.isArray(refreshed?.validItems)
        ? refreshed.validItems
        : lessons;

      const selectedLessons = currentLessons.filter((l) => l.selected);
      const invalidSelected = selectedLessons.filter((l) => !canAddToCart(l));
      const validSelected = selectedLessons.filter((l) => canAddToCart(l));

      if (invalidSelected.length) {
        await Promise.allSettled(
          invalidSelected.map((lesson) => removeLesson(lesson.id))
        );

        if (validSelected.length) {
          alert(
            'חלק מהשיעורים כבר אינם זמינים לרכישה והוסרו מהעגלה. סיכום העסקה עודכן. ניתן לעבור עליו וללחוץ שוב על כפתור הרכישה כדי להמשיך.'
          );
        } else {
          alert('כל השיעורים שנבחרו כבר אינם זמינים לרכישה והוסרו מהעגלה.');
        }

        return;
      }

      if (!validSelected.length) {
        alert('לא נשארו שיעורים זמינים לרכישה.');
        return;
      }

      const validation = await validateCheckoutSelection(validSelected);

      if (!validation?.ok) {
        await refreshCart({ silent: true }).catch(() => {});
        alert(
          validation?.message ||
            'חלק מהשיעורים כבר אינם זמינים לרכישה והוסרו מהעגלה. סיכום העסקה עודכן.'
        );
        return;
      }

      const result = await handleCheckout(paymentMethodOrEvent, {
        lessons: validSelected,
      });

      const ids = result?.lessonIds ?? result?.purchasedLessonIds ?? [];
      if (ids.length) {
        markLessonsPurchased(ids);
      }
    } catch (err) {
      console.error('[CheckoutPage] handleCheckout failed:', err);
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100" dir="rtl">
      <CartHeader />

      <div className="mx-auto max-w-8xl px-2 sm:px-4 md:px-6 lg:px-8 py-4 pb-10">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-start">
          <div className="w-full lg:w-7/12 space-y-4">
            <CartSection
              lessons={lessons}
              isExpanded={expanded.cart}
              onToggleExpand={() => toggleSection('cart')}
              onToggleSelection={toggleLessonSelection}
              onRemoveLesson={removeLesson}
              selectedCount={displaySummary.selectedCount}
              subtotal={displaySummary.subtotal}
            />

            <RecommendedLessons
              recommendedLessons={recommendedLessons}
              isExpanded={expanded.recommendations}
              onToggleExpand={() => toggleSection('recommendations')}
              onAddToCart={addLessonFromOnline}
            />
          </div>

          <div className="w-full lg:w-5/12">
            <div className="sticky top-4 space-y-3 sm:space-y-4">
              {displaySummary.gefenSelectedCount > 0 && (
                <div
                  className="rounded-lg border border-sky-300 bg-sky-50 text-sky-800 text-sm px-3 py-2"
                  role="status"
                >
                  {displaySummary.gefenWalletCovered > 0
                    ? `₪${displaySummary.gefenWalletCovered.toFixed(2)} מתוך עלות שיעורי ${
                        displaySummary.gefenProgramNames.join(', ') || 'תוכנית גפ״ן'
                      } ישולמו מיתרת הארנק${
                        displaySummary.gefenShortfall > 0
                          ? `, וההפרש (₪${displaySummary.gefenShortfall.toFixed(2)}) יתווסף לתשלום הרגיל למטה`
                          : ' — התשלום עבורם הושלם במלואו מהארנק'
                      }.`
                    : `יתרת הארנק של ${
                        displaySummary.gefenProgramNames.join(', ') || 'תוכנית הגפ״ן'
                      } אינה מכסה את השיעורים שנבחרו — מלוא הסכום יתווסף לתשלום הרגיל למטה.`}
                </div>
              )}

              <OrderSummary
                selectedCount={displaySummary.selectedCount}
                subtotal={displaySummary.subtotal}
                creditUsed={displaySummary.creditUsed}
                remainingBeforeVat={displaySummary.remainingBeforeVat}
                TAX_RATE={displaySummary.TAX_RATE}
                vat={displaySummary.vat}
                total={displaySummary.total}
                gefenWalletCovered={displaySummary.gefenWalletCovered}
              />

              <PaymentMethods
                activePayment={activePayment}
                onSelectPayment={setPaymentMethod}
                selectedCount={displaySummary.selectedCount}
                onCheckout={onCheckout}
                isCheckingOut={isCheckingOut}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPageContent;